import React from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faSearch } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { useTeacherSearch } from "hooks/useTeacherSearch";

export const TeacherSearch = ({ onTeacherSelected }) => {
  const { searchTerm, setSearchTerm, searchResults, searchLoading, searchError } = useTeacherSearch();

  const handleSelect = (user) => {
    onTeacherSelected({
      id: user.id,
      name: user.name || user.display_name || user.email,
      email: user.email,
    });
  };

  return (
    <SearchContainer>
      <SearchHeader>Select a Teacher</SearchHeader>
      <SearchInputWrapper>
        <SearchIcon>
          <FontAwesomeIcon icon={faSearch} />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
        />
      </SearchInputWrapper>

      {searchLoading && (
        <LoadingState>
          <FontAwesomeIcon icon={faSpinner} spin color={colors.blue500} />
        </LoadingState>
      )}

      {searchError && <ErrorState>{searchError}</ErrorState>}

      {!searchLoading && searchResults.length > 0 && (
        <ResultsTable>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {searchResults.map((user) => (
              <Tr key={user.id} onClick={() => handleSelect(user)}>
                <Td>{user.name || user.display_name || "—"}</Td>
                <Td>{user.email || "—"}</Td>
                <Td>
                  <SelectButton>Select</SelectButton>
                </Td>
              </Tr>
            ))}
          </tbody>
        </ResultsTable>
      )}

      {!searchLoading && searchTerm.trim() && searchResults.length === 0 && !searchError && (
        <EmptyState>No educators found for "{searchTerm}"</EmptyState>
      )}
    </SearchContainer>
  );
};

const SearchContainer = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SearchHeader = styled.h2`
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.milkGlass800};
  font-size: 14px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 36px;
  border: 1px solid ${colors.blue300};
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${colors.blue500};
    box-shadow: 0 0 0 2px ${colors.blue100 || "rgba(59, 130, 246, 0.1)"};
  }
`;

const LoadingState = styled.div`
  padding: 24px;
  text-align: center;
`;

const ErrorState = styled.div`
  color: ${colors.orange600};
  font-size: 14px;
`;

const EmptyState = styled.div`
  color: ${colors.milkGlass950};
  font-size: 14px;
  padding: 16px 0;
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0px 1px 3px 0px rgba(14, 26, 49, 0.1), 0px 1px 2px 0px rgba(14, 26, 49, 0.06);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.milkGlass800};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.milkGlass600};
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover {
    background-color: ${colors.milkGlass200};
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  border-bottom: 1px solid ${colors.milkGlass600};
`;

const SelectButton = styled.span`
  color: ${colors.blue500};
  font-weight: 500;
  font-size: 14px;
`;
